import type { DecisionAgentSourceClass, ScenarioContract } from './decision-agent-platform';

export type ConstraintContract = Readonly<{
  constraintId: string;
  resourceKind: 'capital_usd' | 'capacity_hours' | 'attention_slots' | 'working_capital_usd';
  capacity: Readonly<{
    value: number;
    unit: string;
    evidenceRefId: string;
  }>;
  window: Readonly<{ from: string; to: string }>;
  source: 'decision_ledger' | 'approved_artifact' | 'officer_attested';
  metric?: ConstraintMetricContract;
}>;

export type ConstraintMetricContract = Readonly<{
  metricKey: string;
  label: string;
  operator: 'gte' | 'lte';
  threshold: Readonly<{
    value: number;
    unit: string;
  }>;
  evidenceRefId: string;
}>;

export type CouplingContract = Readonly<{
  couplingId: string;
  kind: 'substitution' | 'conditional' | 'antagonistic';
  leverRefs: readonly [string, string];
  sharedResource?: string;
  mechanism: string;
  exchangeCurve: readonly CouplingCurvePoint[];
  validity: Readonly<{
    regimeKeys?: readonly string[];
    window?: Readonly<{ from: string; to: string }>;
  }>;
}>;

export type CouplingCurvePoint = Readonly<{
  atAllocation: number;
  objectiveA: CouplingCurveObjective;
  objectiveB: CouplingCurveObjective;
  assumptionRefs: readonly string[];
  constraintMetrics?: readonly CouplingCurveConstraintMetric[];
}>;

export type CouplingCurveConstraintMetric = Readonly<{
  metricKey: string;
  value: number;
  unit: string;
  evidenceRefIds: readonly string[];
}>;

export type CouplingCurveObjective = Readonly<{
  value: number;
  evidenceRefIds: readonly string[];
  basis: 'evaluated' | 'assumed';
}>;

export type TradeoffFrontierEnvelope = Readonly<{
  status: 'frontier' | 'comparison_only' | 'blocked';
  constraint: ConstraintContract;
  couplings: readonly string[];
  points: readonly TradeoffFrontierPoint[];
  currentPosition?: Readonly<{
    pointId: string | null;
    offFrontierGapUsd?: number;
  }>;
  exchangeRateClaims: readonly TradeoffExchangeRateClaim[];
  gaps: readonly TradeoffFrontierGap[];
}>;

export type TradeoffFrontierPointSourceClass = 'scenario' | 'modeled';

export type TradeoffFrontierPoint = Readonly<{
  pointId: string;
  allocation: Readonly<Record<string, number>>;
  objectives: Readonly<Record<string, TradeoffFrontierObjectiveValue>>;
  scenarioRefs: readonly string[];
  dominated: boolean;
  sourceClass: TradeoffFrontierPointSourceClass;
  provenance: TradeoffPointProvenance;
  constraintViolations?: readonly TradeoffConstraintViolation[];
}>;

export type TradeoffConstraintViolation = Readonly<{
  constraintId: string;
  metricKey: string;
  label: string;
  operator: ConstraintMetricContract['operator'];
  threshold: number;
  observedValue: number;
  unit: string;
  evidenceRefIds: readonly string[];
}>;

export type TradeoffFrontierObjectiveValue = Readonly<{
  value: number;
  unit: string;
  numericBindingIds: readonly string[];
}>;

export type TradeoffPointProvenance = Readonly<{
  evaluatorRunIds: readonly string[];
  evidenceRefIds: readonly string[];
  numericBindingIds: readonly string[];
  sourceClasses: readonly string[];
  assumptionRefs: readonly string[];
}>;

export type TradeoffExchangeRateClaim = Readonly<{
  fromObjective: string;
  toObjective: string;
  rate: Readonly<{ value: number; unit: string }>;
  validRange: readonly [number, number];
  numericBindingIds: readonly string[];
  assumptionRefs: readonly string[];
  sourceClass: TradeoffFrontierPointSourceClass;
  provenance: TradeoffPointProvenance;
}>;

export type TradeoffFrontierGapCode =
  | 'constraint_unevidenced'
  | 'blend_refused'
  | 'coupling_evidence_missing'
  | 'coupling_partially_assumed'
  | 'evaluator_missing'
  | 'evaluator_refused'
  | 'metric_binding_missing'
  | 'insufficient_frontier_points';

export type TradeoffFrontierGap = Readonly<{
  code: TradeoffFrontierGapCode;
  label: string;
  pointId?: string;
  evaluatorId?: string;
  missingKeys?: readonly string[];
}>;

export type TradeoffObjectiveDirection = 'maximize' | 'minimize';

export type TradeoffObjectiveEvaluation<TState = unknown> = Readonly<{
  objectiveId: string;
  leverRef: string;
  evaluatorId: string;
  moduleId: string;
  scenarioId: string;
  metricKey: string;
  unit: string;
  direction: TradeoffObjectiveDirection;
  scenario: ScenarioContract;
  state?: TState;
}>;

export type BuildTradeoffFrontierInput = Readonly<{
  constraint: ConstraintContract;
  coupling: CouplingContract | null;
  objectives: readonly [TradeoffObjectiveEvaluation, TradeoffObjectiveEvaluation];
  evaluators: Readonly<Record<string, TradeoffEvaluator>>;
  asOf: string;
  currentPositionPointId?: string | null;
}>;

export type TradeoffEvaluator = Readonly<{
  evaluatorId: string;
  run: (input: TradeoffEvaluatorRunInput) => Promise<TradeoffEvaluatorResult>;
}>;

export type TradeoffEvaluatorRunInput = Readonly<{
  objective: TradeoffObjectiveEvaluation;
  allocation: Readonly<Record<string, number>>;
  pointId: string;
  asOf: string;
}>;

export type TradeoffEvaluatorResult = TradeoffEvaluatorOkResult | TradeoffEvaluatorBlockedResult;

export type TradeoffEvaluatorOkResult = Readonly<{
  status: 'ok';
  evaluatorId: string;
  scenarioRef: string;
  evaluatorRunId: string;
  metrics: Readonly<Record<string, TradeoffFrontierObjectiveValue>>;
  evidenceRefIds: readonly string[];
  sourceClasses: readonly string[];
  sourceClass: TradeoffFrontierPointSourceClass;
  assumptionRefs: readonly string[];
}>;

export type TradeoffEvaluatorBlockedResult = Readonly<{
  status: 'blocked';
  evaluatorId: string;
  reasonCode: string;
  label: string;
  missingKeys?: readonly string[];
}>;

export type TradeoffRuntimeEvidenceRef = Readonly<{
  evidenceRefId: string;
  sourceClass: DecisionAgentSourceClass | string;
}>;

export type TradeoffScenarioOutputLike = Readonly<{
  scenarioId: string;
  label: string;
  objective?: string;
  recommendation: string;
  metrics: Readonly<Record<string, number>>;
  riskLevel: 'low' | 'medium' | 'high';
}>;

export type TradeoffScenarioRunnerOkResult = Readonly<{
  status: 'ok';
  payload: TradeoffScenarioOutputLike;
  evidenceRefs: readonly TradeoffRuntimeEvidenceRef[];
}>;

export type TradeoffScenarioRunnerBlockedResult = Readonly<{
  status: 'blocked';
  reasonCode: string;
  message: string;
  missingKeys?: readonly string[];
}>;

export type TradeoffScenarioRunnerResult =
  | TradeoffScenarioRunnerOkResult
  | TradeoffScenarioRunnerBlockedResult;

export type TradeoffScenarioRunnerInput<TState = unknown> = Readonly<{
  moduleId: string;
  scenarioId: string;
  state: TState | undefined;
  asOf: string;
  allocation: Readonly<Record<string, number>>;
  pointId: string;
}>;

export type TradeoffScenarioRunnerLike<TState = unknown> = Readonly<{
  run: (
    input: TradeoffScenarioRunnerInput<TState>,
    scenario: ScenarioContract
  ) => Promise<TradeoffScenarioRunnerResult>;
}>;

export type TradeoffMetricBinding = Readonly<{
  unit: string;
  numericBindingIds: readonly string[];
}>;

export type AdaptScenarioRunnerEvaluatorInput<TState = unknown> = Readonly<{
  evaluatorId: string;
  runner: TradeoffScenarioRunnerLike<TState>;
  metricBindings: Readonly<Record<string, TradeoffMetricBinding>>;
}>;

export function adaptDecisionAgentScenarioRunnerEvaluator<TState = unknown>(
  input: AdaptScenarioRunnerEvaluatorInput<TState>
): TradeoffEvaluator {
  return Object.freeze({
    evaluatorId: input.evaluatorId,
    run: async (runInput) => {
      const objective = runInput.objective as TradeoffObjectiveEvaluation<TState>;
      const result = await input.runner.run(
        {
          moduleId: objective.moduleId,
          scenarioId: objective.scenarioId,
          state: objective.state,
          asOf: runInput.asOf,
          allocation: runInput.allocation,
          pointId: runInput.pointId,
        },
        objective.scenario
      );
      if (result.status === 'blocked') {
        return {
          status: 'blocked',
          evaluatorId: input.evaluatorId,
          reasonCode: result.reasonCode,
          label: result.message,
          missingKeys: result.missingKeys ?? [],
        };
      }

      const metricValue = result.payload.metrics[objective.metricKey];
      const binding = input.metricBindings[objective.metricKey];
      if (!binding || typeof metricValue !== 'number' || !Number.isFinite(metricValue)) {
        return {
          status: 'blocked',
          evaluatorId: input.evaluatorId,
          reasonCode: 'metric_binding_missing',
          label: `Evaluator ${input.evaluatorId} did not return a bound metric for ${objective.metricKey}.`,
          missingKeys: [`metric:${objective.metricKey}`],
        };
      }

      const evidenceRefIds = uniqueStrings(result.evidenceRefs.map((ref) => ref.evidenceRefId));
      const sourceClasses = uniqueStrings(result.evidenceRefs.map((ref) => ref.sourceClass));
      return {
        status: 'ok',
        evaluatorId: input.evaluatorId,
        scenarioRef: `${objective.moduleId}:${result.payload.scenarioId}`,
        evaluatorRunId: [input.evaluatorId, runInput.pointId, result.payload.scenarioId].join(':'),
        metrics: {
          [objective.metricKey]: {
            value: metricValue,
            unit: binding.unit,
            numericBindingIds: uniqueStrings(binding.numericBindingIds),
          },
        },
        evidenceRefIds,
        sourceClasses,
        sourceClass: 'scenario',
        assumptionRefs: [],
      };
    },
  });
}

export async function buildTradeoffFrontier(
  input: BuildTradeoffFrontierInput
): Promise<TradeoffFrontierEnvelope> {
  const constraintGap = validateConstraintEvidence(input.constraint);
  if (constraintGap) {
    return envelope('blocked', input.constraint, input.coupling, [], [], [constraintGap]);
  }

  const unitGap = validateObjectiveUnits(input.objectives);
  if (unitGap) {
    return envelope('blocked', input.constraint, input.coupling, [], [], [unitGap]);
  }

  if (!input.coupling || input.coupling.exchangeCurve.length === 0) {
    return buildComparisonOnlyEnvelope(input);
  }

  const constraintMetricGap = validateConstraintMetricEvidence(input.constraint, input.coupling);
  if (constraintMetricGap) {
    return buildComparisonOnlyEnvelope(input, [constraintMetricGap], false);
  }

  const gaps: TradeoffFrontierGap[] = [];
  if (hasAssumedCurvePoints(input.coupling)) {
    gaps.push({
      code: 'coupling_partially_assumed',
      label:
        'Coupling curve contains named assumptions; frontier may render only with assumption cards.',
    });
  }

  const points: TradeoffFrontierPoint[] = [];
  for (const curvePoint of input.coupling.exchangeCurve) {
    const pointId = `${input.coupling.couplingId}:${curvePoint.atAllocation}`;
    const allocation = allocationForCurvePoint(input.coupling, curvePoint);
    const result = await evaluatePoint({
      input,
      allocation,
      pointId,
      assumptionRefs: curvePoint.assumptionRefs,
      constraintMetrics: curvePoint.constraintMetrics ?? [],
    });
    if (result.status === 'blocked') {
      gaps.push(...result.gaps);
      continue;
    }
    points.push(result.point);
  }

  if (points.length === 0) {
    return envelope('blocked', input.constraint, input.coupling, [], [], gaps);
  }

  const markedPoints = markDominated(points, input.objectives);
  const exchangeRateClaims = buildExchangeRateClaims(markedPoints, input.objectives);
  if (markedPoints.filter((point) => !point.dominated).length < 2) {
    gaps.push({
      code: 'insufficient_frontier_points',
      label:
        'At least two efficient evaluated points are required before exchange-rate claims can render.',
    });
  }

  return envelope(
    'frontier',
    input.constraint,
    input.coupling,
    markedPoints,
    exchangeRateClaims,
    gaps,
    input.currentPositionPointId
  );
}

async function buildComparisonOnlyEnvelope(
  input: BuildTradeoffFrontierInput,
  extraGaps: readonly TradeoffFrontierGap[] = [],
  includeCouplingGap = true
): Promise<TradeoffFrontierEnvelope> {
  const gaps: TradeoffFrontierGap[] = [...extraGaps];
  if (includeCouplingGap) {
    gaps.push({
      code: 'coupling_evidence_missing',
      label:
        'No governed coupling evidence is bound; frontier and exchange-rate claims are withheld.',
    });
  }
  const points: TradeoffFrontierPoint[] = [];
  for (const objective of input.objectives) {
    const pointId = `comparison:${objective.objectiveId}`;
    const result = await evaluatePoint({
      input,
      allocation: { [objective.leverRef]: 1 },
      pointId,
      assumptionRefs: [],
      objectiveOverride: objective,
    });
    if (result.status === 'blocked') {
      gaps.push(...result.gaps);
      continue;
    }
    points.push(result.point);
  }
  return envelope('comparison_only', input.constraint, null, points, [], gaps);
}

async function evaluatePoint(args: {
  input: BuildTradeoffFrontierInput;
  allocation: Readonly<Record<string, number>>;
  pointId: string;
  assumptionRefs: readonly string[];
  constraintMetrics?: readonly CouplingCurveConstraintMetric[];
  objectiveOverride?: TradeoffObjectiveEvaluation;
}): Promise<
  | Readonly<{ status: 'ok'; point: TradeoffFrontierPoint }>
  | Readonly<{ status: 'blocked'; gaps: readonly TradeoffFrontierGap[] }>
> {
  const objectives = args.objectiveOverride ? [args.objectiveOverride] : args.input.objectives;
  const results: TradeoffEvaluatorOkResult[] = [];
  const gaps: TradeoffFrontierGap[] = [];
  for (const objective of objectives) {
    const evaluator = args.input.evaluators[objective.evaluatorId];
    if (!evaluator) {
      gaps.push({
        code: 'evaluator_missing',
        label: `No evaluator is registered for ${objective.evaluatorId}.`,
        pointId: args.pointId,
        evaluatorId: objective.evaluatorId,
      });
      continue;
    }
    const result = await evaluator.run({
      objective,
      allocation: args.allocation,
      pointId: args.pointId,
      asOf: args.input.asOf,
    });
    if (result.status === 'blocked') {
      gaps.push({
        code:
          result.reasonCode === 'metric_binding_missing'
            ? 'metric_binding_missing'
            : 'evaluator_refused',
        label: result.label,
        pointId: args.pointId,
        evaluatorId: result.evaluatorId,
        missingKeys: result.missingKeys ?? [],
      });
      continue;
    }
    results.push(result);
  }

  if (gaps.length > 0 || results.length !== objectives.length) {
    return { status: 'blocked', gaps };
  }

  const objectiveValues: Record<string, TradeoffFrontierObjectiveValue> = {};
  for (let index = 0; index < objectives.length; index += 1) {
    const objective = objectives[index];
    const result = results[index];
    const value = result.metrics[objective.metricKey];
    if (!value) {
      return {
        status: 'blocked',
        gaps: [
          {
            code: 'metric_binding_missing',
            label: `Evaluator ${result.evaluatorId} omitted ${objective.metricKey}.`,
            pointId: args.pointId,
            evaluatorId: result.evaluatorId,
            missingKeys: [`metric:${objective.metricKey}`],
          },
        ],
      };
    }
    objectiveValues[objective.objectiveId] = value;
  }

  const provenance = mergeProvenance(results, args.assumptionRefs);
  const constraintViolations = constraintViolationsForPoint(
    args.input.constraint,
    args.constraintMetrics ?? []
  );
  return {
    status: 'ok',
    point: Object.freeze({
      pointId: args.pointId,
      allocation: Object.freeze({ ...args.allocation }),
      objectives: Object.freeze(objectiveValues),
      scenarioRefs: uniqueStrings(results.map((result) => result.scenarioRef)),
      dominated: false,
      sourceClass: mergePointSourceClass(results),
      provenance,
      constraintViolations,
    }),
  };
}

function envelope(
  status: TradeoffFrontierEnvelope['status'],
  constraint: ConstraintContract,
  coupling: CouplingContract | null,
  points: readonly TradeoffFrontierPoint[],
  exchangeRateClaims: readonly TradeoffExchangeRateClaim[],
  gaps: readonly TradeoffFrontierGap[],
  currentPositionPointId?: string | null
): TradeoffFrontierEnvelope {
  return Object.freeze({
    status,
    constraint,
    couplings: coupling ? [coupling.couplingId] : [],
    points: Object.freeze([...points]),
    currentPosition:
      currentPositionPointId === undefined ? undefined : { pointId: currentPositionPointId },
    exchangeRateClaims: Object.freeze([...exchangeRateClaims]),
    gaps: Object.freeze([...gaps]),
  });
}

function validateConstraintEvidence(constraint: ConstraintContract): TradeoffFrontierGap | null {
  if (
    !Number.isFinite(constraint.capacity.value) ||
    constraint.capacity.value <= 0 ||
    !constraint.capacity.unit.trim() ||
    !constraint.capacity.evidenceRefId.trim()
  ) {
    return {
      code: 'constraint_unevidenced',
      label:
        'Constraint capacity must be finite, positive, unit-bound, and evidence-bound before a trade-off frontier can run.',
    };
  }
  return null;
}

function validateConstraintMetricEvidence(
  constraint: ConstraintContract,
  coupling: CouplingContract
): TradeoffFrontierGap | null {
  const metric = constraint.metric;
  if (!metric) return null;
  if (
    !metric.metricKey.trim() ||
    !metric.label.trim() ||
    !Number.isFinite(metric.threshold.value) ||
    !metric.threshold.unit.trim() ||
    !metric.evidenceRefId.trim()
  ) {
    return {
      code: 'constraint_unevidenced',
      label: `${metric.label} constraint evidence is absent; Ask Helix can compare options but will not publish a constrained frontier.`,
      missingKeys: [`constraint:${metric.metricKey}`],
    };
  }

  const hasPointEvidence = coupling.exchangeCurve.some((point) =>
    (point.constraintMetrics ?? []).some(
      (candidate) =>
        candidate.metricKey === metric.metricKey &&
        Number.isFinite(candidate.value) &&
        candidate.unit.trim().length > 0 &&
        candidate.evidenceRefIds.some((ref) => ref.trim().length > 0)
    )
  );
  if (!hasPointEvidence) {
    return {
      code: 'constraint_unevidenced',
      label: `${metric.label} constraint evidence is absent from the approved coupling curve; Ask Helix can compare options but will not publish a constrained frontier.`,
      missingKeys: [`constraint:${metric.metricKey}`],
    };
  }
  return null;
}

function validateObjectiveUnits(
  objectives: readonly [TradeoffObjectiveEvaluation, TradeoffObjectiveEvaluation]
): TradeoffFrontierGap | null {
  if (objectives[0].unit !== objectives[1].unit) {
    return {
      code: 'blend_refused',
      label: `Trade-off exchange rates require commensurable units; ${objectives[0].unit} cannot blend with ${objectives[1].unit}.`,
    };
  }
  return null;
}

function hasAssumedCurvePoints(coupling: CouplingContract): boolean {
  return coupling.exchangeCurve.some(
    (point) => point.objectiveA.basis === 'assumed' || point.objectiveB.basis === 'assumed'
  );
}

function constraintViolationsForPoint(
  constraint: ConstraintContract,
  observations: readonly CouplingCurveConstraintMetric[]
): readonly TradeoffConstraintViolation[] {
  const metric = constraint.metric;
  if (!metric) return [];
  const observation = observations.find((candidate) => candidate.metricKey === metric.metricKey);
  if (!observation || !Number.isFinite(observation.value)) return [];
  const violates =
    metric.operator === 'gte'
      ? observation.value < metric.threshold.value
      : observation.value > metric.threshold.value;
  if (!violates) return [];
  return [
    Object.freeze({
      constraintId: constraint.constraintId,
      metricKey: metric.metricKey,
      label: metric.label,
      operator: metric.operator,
      threshold: metric.threshold.value,
      observedValue: observation.value,
      unit: observation.unit || metric.threshold.unit,
      evidenceRefIds: uniqueStrings([
        metric.evidenceRefId,
        ...observation.evidenceRefIds,
      ]),
    }),
  ];
}

function allocationForCurvePoint(
  coupling: CouplingContract,
  curvePoint: CouplingCurvePoint
): Readonly<Record<string, number>> {
  return Object.freeze({
    [coupling.leverRefs[0]]: curvePoint.atAllocation,
    [coupling.leverRefs[1]]: 1 - curvePoint.atAllocation,
  });
}

function markDominated(
  points: readonly TradeoffFrontierPoint[],
  objectives: readonly [TradeoffObjectiveEvaluation, TradeoffObjectiveEvaluation]
): readonly TradeoffFrontierPoint[] {
  return Object.freeze(
    points.map((point) =>
      Object.freeze({
        ...point,
        dominated: points.some(
          (candidate) =>
            candidate.pointId !== point.pointId && dominates(candidate, point, objectives)
        ),
      })
    )
  );
}

function dominates(
  candidate: TradeoffFrontierPoint,
  target: TradeoffFrontierPoint,
  objectives: readonly TradeoffObjectiveEvaluation[]
): boolean {
  let strictlyBetter = false;
  for (const objective of objectives) {
    const candidateValue = candidate.objectives[objective.objectiveId]?.value;
    const targetValue = target.objectives[objective.objectiveId]?.value;
    if (candidateValue === undefined || targetValue === undefined) return false;
    if (objective.direction === 'maximize') {
      if (candidateValue < targetValue) return false;
      if (candidateValue > targetValue) strictlyBetter = true;
    } else {
      if (candidateValue > targetValue) return false;
      if (candidateValue < targetValue) strictlyBetter = true;
    }
  }
  return strictlyBetter;
}

function buildExchangeRateClaims(
  points: readonly TradeoffFrontierPoint[],
  objectives: readonly [TradeoffObjectiveEvaluation, TradeoffObjectiveEvaluation]
): readonly TradeoffExchangeRateClaim[] {
  const efficientPoints = points
    .filter((point) => !point.dominated)
    .sort(
      (left, right) =>
        allocationForObjective(left, objectives[0]) - allocationForObjective(right, objectives[0])
    );
  const claims: TradeoffExchangeRateClaim[] = [];
  for (let index = 0; index < efficientPoints.length - 1; index += 1) {
    const current = efficientPoints[index];
    const next = efficientPoints[index + 1];
    const fromCurrent = current.objectives[objectives[0].objectiveId]?.value;
    const fromNext = next.objectives[objectives[0].objectiveId]?.value;
    const toCurrent = current.objectives[objectives[1].objectiveId]?.value;
    const toNext = next.objectives[objectives[1].objectiveId]?.value;
    if (
      fromCurrent === undefined ||
      fromNext === undefined ||
      toCurrent === undefined ||
      toNext === undefined ||
      fromNext === fromCurrent
    ) {
      continue;
    }
    claims.push(
      Object.freeze({
        fromObjective: objectives[0].objectiveId,
        toObjective: objectives[1].objectiveId,
        rate: {
          value: roundRate((toNext - toCurrent) / (fromNext - fromCurrent)),
          unit: `${objectives[1].unit} per ${objectives[0].unit}`,
        },
        validRange: [
          allocationForObjective(current, objectives[0]),
          allocationForObjective(next, objectives[0]),
        ] as const,
        numericBindingIds: uniqueStrings([
          ...current.provenance.numericBindingIds,
          ...next.provenance.numericBindingIds,
        ]),
        assumptionRefs: uniqueStrings([
          ...current.provenance.assumptionRefs,
          ...next.provenance.assumptionRefs,
        ]),
        sourceClass: mergeClaimSourceClass(current, next),
        provenance: mergePointProvenance(current.provenance, next.provenance),
      })
    );
  }
  return Object.freeze(claims);
}

function allocationForObjective(
  point: TradeoffFrontierPoint,
  objective: TradeoffObjectiveEvaluation
): number {
  return point.allocation[objective.leverRef] ?? 0;
}

function mergeProvenance(
  results: readonly TradeoffEvaluatorOkResult[],
  assumptionRefs: readonly string[]
): TradeoffPointProvenance {
  return Object.freeze({
    evaluatorRunIds: uniqueStrings(results.map((result) => result.evaluatorRunId)),
    evidenceRefIds: uniqueStrings(results.flatMap((result) => result.evidenceRefIds)),
    numericBindingIds: uniqueStrings(
      results.flatMap((result) =>
        Object.values(result.metrics).flatMap((metric) => metric.numericBindingIds)
      )
    ),
    sourceClasses: uniqueStrings(results.flatMap((result) => result.sourceClasses)),
    assumptionRefs: uniqueStrings(assumptionRefs),
  });
}

function mergePointProvenance(
  left: TradeoffPointProvenance,
  right: TradeoffPointProvenance
): TradeoffPointProvenance {
  return Object.freeze({
    evaluatorRunIds: uniqueStrings([...left.evaluatorRunIds, ...right.evaluatorRunIds]),
    evidenceRefIds: uniqueStrings([...left.evidenceRefIds, ...right.evidenceRefIds]),
    numericBindingIds: uniqueStrings([...left.numericBindingIds, ...right.numericBindingIds]),
    sourceClasses: uniqueStrings([...left.sourceClasses, ...right.sourceClasses]),
    assumptionRefs: uniqueStrings([...left.assumptionRefs, ...right.assumptionRefs]),
  });
}

function mergePointSourceClass(
  results: readonly TradeoffEvaluatorOkResult[]
): TradeoffFrontierPointSourceClass {
  return results.some((result) => result.sourceClass === 'modeled') ? 'modeled' : 'scenario';
}

function mergeClaimSourceClass(
  left: TradeoffFrontierPoint,
  right: TradeoffFrontierPoint
): TradeoffFrontierPointSourceClass {
  return left.sourceClass === 'modeled' || right.sourceClass === 'modeled' ? 'modeled' : 'scenario';
}

function uniqueStrings(values: readonly (string | null | undefined)[]): string[] {
  return Array.from(
    new Set(values.map((value) => value?.trim() ?? '').filter((value) => value.length > 0))
  );
}

function roundRate(value: number): number {
  return Math.round(value * 1_000_000) / 1_000_000;
}
